import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faEdit,
	faTrash,
	faUserFriends,
	faCheckCircle,
	faUserSlash,
	faUser,
	faCalendarAlt,
	faBookOpen,
	faSchool,
	faLayerGroup,
	faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Loading from "../../common/Loading";
import { useLogDetails } from "./hooks/useLogDetails";
import { formatDate } from "../../../utils/dateUtils";
import styles from "./LogDetailsModal.module.css";

const LogDetailsModal = ({
	isOpen,
	onClose,
	logId,
	log,
	onEdit,
	onDelete,
	isEditable = true,
	fromOverviewPage = false,
	openTimestamp,
}) => {
	const { logDetails, loading, error, refetch } = useLogDetails(logId, log);

	useEffect(() => {
		if (isOpen && logId) {
			refetch();
		}
	}, [isOpen, logId, refetch, openTimestamp]);

	const handleDelete = () => {
		if (window.confirm("Are you sure you want to delete this log?")) {
			const logIdToDelete = logDetails?.classLog?.id || logDetails?.id || logId;
			onDelete(logIdToDelete);
			onClose();
		}
	};

	const handleEdit = () => {
		onEdit(logDetails);
		onClose();
	};

	const footer = (
		<>
			{isEditable && onEdit && (
				<Button variant="primary" onClick={handleEdit}>
					<FontAwesomeIcon icon={faEdit} />
					Edit
				</Button>
			)}

			{isEditable && onDelete && (
				<Button variant="danger" onClick={handleDelete}>
					<FontAwesomeIcon icon={faTrash} />
					Delete
				</Button>
			)}

			<Button variant="secondary" onClick={onClose}>
				Close
			</Button>
		</>
	);

	const renderContent = () => {
		if (loading) {
			return <Loading message="Loading log details..." />;
		}

		if (error) {
			return (
				<div className={styles.errorState}>
					<FontAwesomeIcon
						icon={faExclamationCircle}
						className={styles.errorIcon}
					/>
					<p>Error loading log details: {error.message || error}</p>
				</div>
			);
		}

		if (!logDetails) {
			return (
				<div className={styles.errorState}>
					<p>No details available</p>
				</div>
			);
		}

		// Extract data from the backend response structure
		const classLog = logDetails.classLog || logDetails;
		const teacherName = logDetails.teacherName || "Unknown Teacher";
		const subjectName = logDetails.subjectName || "Unknown Subject";
		const departmentName = logDetails.departmentName || "Unknown Class";
		const absentStudents = logDetails.absentStudents || [];

		return (
			<>
				<div className={styles.detailsGrid}>
					<div className={styles.detailRow}>
						<div className={styles.detailLabel}>
							<FontAwesomeIcon
								icon={faCalendarAlt}
								className={styles.detailIcon}
							/>
							Date
						</div>
						<div className={styles.detailValue}>
							{classLog.classDate
								? formatDate(classLog.classDate)
								: "No date available"}
						</div>
					</div>

					<div className={styles.detailRow}>
						<div className={styles.detailLabel}>
							<FontAwesomeIcon
								icon={faBookOpen}
								className={styles.detailIcon}
							/>
							Subject
						</div>
						<div className={styles.detailValue}>{subjectName}</div>
					</div>

					<div className={styles.detailRow}>
						<div className={styles.detailLabel}>
							<FontAwesomeIcon
								icon={faLayerGroup}
								className={styles.detailIcon}
							/>
							Period
						</div>
						<div className={styles.detailValue}>
							Period {classLog.period || "?"}
						</div>
					</div>

					<div className={styles.detailRow}>
						<div className={styles.detailLabel}>
							<FontAwesomeIcon
								icon={faLayerGroup}
								className={styles.detailIcon}
							/>
							Sequence
						</div>
						<div className={styles.detailValue}>
							#{classLog.sequence || "?"}
						</div>
					</div>

					{!fromOverviewPage && (
						<>
							<div className={styles.detailRow}>
								<div className={styles.detailLabel}>
									<FontAwesomeIcon
										icon={faUser}
										className={styles.detailIcon}
									/>
									Teacher
								</div>
								<div className={styles.detailValue}>{teacherName}</div>
							</div>

							<div className={styles.detailRow}>
								<div className={styles.detailLabel}>
									<FontAwesomeIcon
										icon={faSchool}
										className={styles.detailIcon}
									/>
									Class
								</div>
								<div className={styles.detailValue}>{departmentName}</div>
							</div>
						</>
					)}
				</div>

				<div className={styles.attendanceSection}>
					<h4>
						<FontAwesomeIcon icon={faUserFriends} />
						<span>Student Attendance</span>
					</h4>

					{absentStudents && absentStudents.length > 0 ? (
						<>
							<div className={styles.attendanceSummary}>
								<FontAwesomeIcon
									icon={faUserSlash}
									className={`${styles.summaryIcon} ${styles.absent}`}
								/>
								<span>{absentStudents.length} students absent</span>
							</div>

							<div className={styles.absentStudents}>
								{absentStudents.map((student, index) => (
									<div
										key={student.studentId || index}
										className={styles.absentStudent}
									>
										<FontAwesomeIcon icon={faUserSlash} />
										<span>{student.name}</span>
									</div>
								))}
							</div>
						</>
					) : (
						<div className={`${styles.attendanceSummary} ${styles.full}`}>
							<FontAwesomeIcon
								icon={faCheckCircle}
								className={`${styles.summaryIcon} ${styles.present}`}
							/>
							<span>All students present</span>
						</div>
					)}
				</div>
			</>
		);
	};

	// Extract title from the appropriate place
	const title =
		logDetails?.classLog?.lectureTitle ||
		logDetails?.lectureTitle ||
		"Class Log Details";

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			footer={footer}
			size="medium"
		>
			{renderContent()}
		</Modal>
	);
};

export default LogDetailsModal;
